import { Flex } from 'antd';
import React from 'react';

import styles from './status-card.styles';

/**
 * Define la variante visual de la StatusCard, que normalmente corresponde a un estado específico.
 */
export type StatusCardVariant =
  | 'created'
  | 'in-progress'
  | 'partially-delivered'
  | 'delivereds'
  | 'overdue';

/**
 * Props para el componente StatusCard.
 */
export type StatusCardProps = {
  /**
   * El valor numérico que se mostrará de forma destacada en la tarjeta.
   */
  count: number;
  /**
   * La etiqueta o título que describe el estado.
   */
  label: string;
  /**
   * La variante visual de la tarjeta, que determina su combinación de colores.
   */
  variant: StatusCardVariant;
  /**
   * Si es verdadero, la tarjeta se mostrará en un estado 'activo', a menudo con una sombra o borde más prominente.
   *
   * @default false
   */
  active?: boolean;
  /**
   * Si es verdadero, la tarjeta se desactivará visualmente y no se podrá hacer clic en ella.
   *
   * @default false
   */
  disabled?: boolean;
  /**
   * Una función de devolución de llamada opcional que se ejecutará cuando se haga clic en la tarjeta.
   * La tarjeta solo se puede hacer clic si no está deshabilitada.
   */
  onClick?: () => void;
  /**
   * Un nombre de clase CSS opcional para aplicar al contenedor de la tarjeta.
   */
  className?: string;
  /**
   * Estilos personalizados opcionales que se aplicarán al contenedor de la tarjeta.
   */
  style?: React.CSSProperties;
};

/**
 * Un componente de tarjeta utilizado para mostrar un estado con un recuento y una etiqueta.
 * Es visualmente distinto según su variante y puede ser interactivo.
 *
 * @param {StatusCardProps} props - Las props para el componente StatusCard.
 *
 * @returns {JSX.Element} El componente StatusCard renderizado.
 */
const StatusCard = ({
  count,
  label,
  variant,
  active = false,
  disabled = false,
  onClick,
  className,
  style
}: StatusCardProps) => {
  const delegatedStyles = {
    ...style
  };

  const handleClick = () => {
    if (!disabled && onClick) {
      onClick();
    }
  };

  return (
    <Flex
      onClick={handleClick}
      className={className}
      style={{
        ...styles.statusCard,
        ...styles.statusCard__variant[variant],
        ...(active ? styles.statusCard__variantActive[variant] : {}),
        ...(disabled ? styles.statusCard__disabled : {}),
        cursor: disabled ? 'not-allowed' : onClick ? 'pointer' : 'default',
        ...delegatedStyles
      }}
      align='center'
      gap={16}>
      <span style={styles.statusCard__count}>{count}</span>
      <span style={styles.statusCard__label}>{label}</span>
    </Flex>
  );
};

export default StatusCard;
